import { forwardRef } from 'react';
import { format } from 'date-fns';

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <tr>
      <td style={{ padding: '6px 12px', fontWeight: 600, fontSize: '11px', color: '#555', width: '180px', verticalAlign: 'top', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</td>
      <td style={{ padding: '6px 12px', fontSize: '13px', color: '#111' }}>{value}</td>
    </tr>
  );
}

function SectionBlock({ title, children }) {
  return (
    <div style={{ marginBottom: '20px', pageBreakInside: 'avoid' }}>
      <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', borderBottom: '2px solid #2563eb', paddingBottom: '6px', marginBottom: '10px', fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
      {children}
    </div>
  );
}

/*
 * Props:
 *   client - full client object
 *   companyName, companyAddress, userName - metadata
 *   documentPages - array of { filename, dataUrl, pageNum?, totalPages? }
 *     Each item = one rendered page (image or PDF page as image)
 */
const PrintLayout = forwardRef(({ client, companyName, companyAddress, userName, documentPages = [] }, ref) => {
  const now = new Date();
  const sectionsOrder = client?.sections_order?.length ? client.sections_order : ['trust_details', 'registration_details', 'udin_details', 'bank_details'];

  const renderSection = (sid) => {
    if (sid === 'trust_details') return (
      <SectionBlock key={sid} title="Trust Details">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          <InfoRow label="Trust Name" value={client.trust_name} />
          <InfoRow label="Trust Number" value={client.trust_number} />
          <InfoRow label="Trust PAN" value={client.trust_pan} />
          <InfoRow label="Trustee Name" value={client.trustee_name} />
          <InfoRow label="Trustee Number" value={client.trustee_number} />
          <InfoRow label="Email" value={client.email} />
          <InfoRow label="Phone" value={client.phone} />
          <InfoRow label="Category" value={client.category} />
          <InfoRow label="Financial Year" value={client.financial_year} />
          <InfoRow label="Status" value={client.status} />
          <InfoRow label="Address" value={client.address} />
        </tbody></table>
      </SectionBlock>
    );
    if (sid === 'registration_details') return (
      <SectionBlock key={sid} title="Registration Details">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          <InfoRow label="12A Number" value={client.registration_12a} />
          <InfoRow label="80G Number" value={client.registration_80g} />
          <InfoRow label="12A Due Date" value={client.due_date_12a ? new Date(client.due_date_12a).toLocaleDateString() : ''} />
          <InfoRow label="80G Due Date" value={client.due_date_80g ? new Date(client.due_date_80g).toLocaleDateString() : ''} />
        </tbody></table>
      </SectionBlock>
    );
    if (sid === 'udin_details') return (
      <SectionBlock key={sid} title="UDIN Registration">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          <InfoRow label="UDIN Number" value={client.udin_number} />
          <InfoRow label="Year" value={client.udin_year} />
        </tbody></table>
      </SectionBlock>
    );
    if (sid === 'bank_details') return (
      <SectionBlock key={sid} title="Bank Details">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          <InfoRow label="Account Number" value={client.bank_account} />
          <InfoRow label="IFSC Code" value={client.bank_ifsc} />
          <InfoRow label="Bank Name" value={client.bank_name} />
        </tbody></table>
      </SectionBlock>
    );
    if (sid === 'documents') return null;
    const cs = client.custom_sections?.find((s) => s.id === sid);
    if (!cs) return null;
    return (
      <SectionBlock key={sid} title={cs.section_name}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
          {cs.fields.map((f) => (
            <InfoRow key={f.id} label={f.label} value={f.type === 'checkbox' ? (f.value ? 'Yes' : 'No') : (f.value || '')} />
          ))}
        </tbody></table>
      </SectionBlock>
    );
  };

  return (
    <div ref={ref} style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: '#111', background: '#fff', width: '210mm', margin: '0 auto' }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* === PAGE 1+: Client Details === */}
      <div style={{ minHeight: '250mm' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '3px solid #1a1a1a', paddingBottom: '15px', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 4px', fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.5px' }}>
            {companyName || 'K. G. Patel & Company'}
          </h1>
          {companyAddress && <p style={{ fontSize: '11px', color: '#555', margin: '0' }}>{companyAddress}</p>}
          <p style={{ fontSize: '10px', color: '#888', margin: '4px 0 0' }}>Income Tax Consultants | Trust Account Management</p>
        </div>

        {/* Client Title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #e5e5e5' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0', fontFamily: 'Manrope, sans-serif' }}>{client.trust_name || 'Client Profile'}</h2>
            <p style={{ fontSize: '12px', color: '#666', margin: '2px 0 0' }}>Trust ID: {client.trust_id}</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: '10px', color: '#888' }}>
            <p style={{ margin: '0' }}>Printed: {format(now, 'dd MMM yyyy, hh:mm a')}</p>
            <p style={{ margin: '2px 0 0' }}>By: {userName || 'Admin'}</p>
          </div>
        </div>

        {/* Sections */}
        {sectionsOrder.map((sid) => renderSection(sid))}

        {/* Footer */}
        <div style={{ marginTop: '30px', paddingTop: '10px', borderTop: '1px solid #ddd', fontSize: '10px', color: '#999', display: 'flex', justifyContent: 'space-between' }}>
          <span>Created: {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'} | Updated: {client.updated_at ? new Date(client.updated_at).toLocaleDateString() : '-'}</span>
          <span>Generated by {companyName || 'K. G. Patel & Company'}</span>
        </div>
      </div>

      {/* === DOCUMENT PAGES: Each document on its own A4 page === */}
      {documentPages.map((page, idx) => (
        <div
          key={idx}
          style={{
            pageBreakBefore: 'always',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: '270mm',
            paddingTop: '10mm',
          }}
        >
          {/* Document header */}
          <div style={{ width: '100%', marginBottom: '8mm', borderBottom: '1px solid #ddd', paddingBottom: '4mm' }}>
            <p style={{ fontSize: '13px', fontWeight: 700, margin: '0', color: '#222' }}>
              {page.filename}
            </p>
            {page.totalPages > 1 && (
              <p style={{ fontSize: '10px', color: '#888', margin: '2px 0 0' }}>
                Page {page.pageNum} of {page.totalPages}
              </p>
            )}
          </div>

          {/* Document content (rendered as image) */}
          <img
            src={page.dataUrl}
            alt={page.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '245mm',
              objectFit: 'contain',
              border: '1px solid #eee',
            }}
          />

          {/* Page footer */}
          <div style={{ marginTop: 'auto', paddingTop: '4mm', width: '100%', textAlign: 'center', fontSize: '9px', color: '#aaa', borderTop: '1px solid #eee' }}>
            Document {idx + 1} of {documentPages.length} | {client.trust_name} ({client.trust_id})
          </div>
        </div>
      ))}
    </div>
  );
});

PrintLayout.displayName = 'PrintLayout';
export default PrintLayout;
